import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';

export type QuickBooksConnectionState =
  | 'CONFIGURATION_UNAVAILABLE'
  | 'DISCONNECTED'
  | 'CONNECTED_VERIFIED'
  | 'AUTHORIZATION_REQUIRES_REPLACEMENT'
  | 'REFRESH_RECONCILIATION_REQUIRED'
  | 'REMOVING'
  | 'REMOVED'
  | 'REVOCATION_UNKNOWN';

export interface QuickBooksConnectionStatus {
  actions: {
    canConnect: boolean;
    canRemove: boolean;
    canRetryCleanup: boolean;
  };
  cleanupRequired: boolean;
  company: {
    companyName: string;
    connectedAt: string | null;
    country: string;
    legalName: string;
    verifiedAt: string | null;
  } | null;
  configurationAvailable: boolean;
  provider: 'QUICKBOOKS_ONLINE';
  state: QuickBooksConnectionState;
}

export type WallitAccountEligibilityReason =
  | 'ACTIVE_NON_ROOT_CHART_ACCOUNT';

export interface WallitAccountCandidate {
  id: string;
  code: string;
  name: string;
  accountTypeCode: 'assets' | 'liabilities' | 'revenue' | 'expenses';
  parent: {id: string; code: string; name: string} | null;
  isSystem: boolean;
  eligibilityReasons: WallitAccountEligibilityReason[];
}

export interface WallitAccountDiagnostic {
  reasonCode: string;
  accountId?: string;
  contractId?: string;
  metadataRole?: string;
}

export interface WallitAccountCandidatesResponse {
  projectionAsOfDate: string;
  accounts: WallitAccountCandidate[];
  diagnostics: WallitAccountDiagnostic[];
}

export interface QuickBooksAccount {
  id: string;
  syncToken: string;
  name: string;
  fullyQualifiedName: string;
  acctNum?: string;
  classification: string;
  accountType: string;
  accountSubType: string | null;
  subAccount: boolean;
  parentRef: {id: string; name: string | null} | null;
  active: boolean | null;
  currencyRef: {value: string; name: string | null} | null;
  lastUpdatedAt?: string;
}

export type QuickBooksAccountHintResult =
  | 'EXACT_NAME_AND_CLASSIFICATION'
  | 'INACTIVE_EXACT_NAME'
  | 'CLASSIFICATION_CONFLICT'
  | 'AMBIGUOUS'
  | 'MISSING';

export interface QuickBooksAccountHint {
  wallitAccountId: string;
  result: QuickBooksAccountHintResult;
  quickBooksAccountIds: string[];
  sharedQuickBooksAccountIds: string[];
}

export interface QuickBooksAccountCatalogResponse {
  retrievedAt: string;
  projectionAsOfDate: string;
  complete: true;
  accounts: QuickBooksAccount[];
  hints: QuickBooksAccountHint[];
}

export interface WallitSupplierCandidate {
  sourceType: 'WALLIT_VENDOR';
  sourceId: string;
  displayName: string;
  operationalKey: string;
}

export interface WallitCompanyCandidate {
  sourceType: 'WALLIT_TEAMCOMPANY';
  sourceId: string;
  displayName: string;
  classification?: string;
}

export interface WallitSupplierCandidatesResponse {
  complete: true;
  suppliers: WallitSupplierCandidate[];
}

export interface WallitCompanyCandidatesResponse {
  complete: true;
  companies: WallitCompanyCandidate[];
}

export type QuickBooksPartyHintResult =
  | 'EXACT_NAME'
  | 'INACTIVE_EXACT_NAME'
  | 'MULTIPLE_EXACT_NAME_MATCHES'
  | 'NO_EXACT_NAME';

export interface QuickBooksPartyHint {
  wallitSourceId: string;
  result: QuickBooksPartyHintResult;
  quickBooksEntityIds: string[];
  duplicateWallitSourceIds: string[];
  duplicateQuickBooksEntityIds: string[];
}

interface QuickBooksPartyBase {
  id: string;
  displayName: string;
  companyName?: string;
  active: boolean;
  lastUpdatedAt?: string;
}

export interface QuickBooksVendor extends QuickBooksPartyBase {
  entityType: 'VENDOR';
}

export interface QuickBooksCustomer extends QuickBooksPartyBase {
  entityType: 'CUSTOMER';
  fullyQualifiedName?: string;
  parentRef?: {id: string; displayName?: string};
}

export interface QuickBooksVendorCatalogResponse {
  complete: true;
  retrievedAt: string;
  vendors: QuickBooksVendor[];
  hints: QuickBooksPartyHint[];
}

export interface QuickBooksCustomerCatalogResponse {
  complete: true;
  retrievedAt: string;
  customers: QuickBooksCustomer[];
  hints: QuickBooksPartyHint[];
}

interface AuthorizationStartResponse {
  authorizationUrl: string;
}

@Injectable({providedIn: 'root'})
export class QuickBooksConnectionsService {
  constructor(private restangular: ApiClient) {}

  getStatus(): Promise<QuickBooksConnectionStatus> {
    return this.quickBooksResource().one('connection').get().toPromise() as Promise<QuickBooksConnectionStatus>;
  }

  getWallitAccountCandidates(): Promise<WallitAccountCandidatesResponse> {
    return this.quickBooksResource().one('wallit-account-candidates').get().toPromise() as
      Promise<WallitAccountCandidatesResponse>;
  }

  getAccountCatalog(): Promise<QuickBooksAccountCatalogResponse> {
    return this.quickBooksResource().one('account-catalog').get().toPromise() as
      Promise<QuickBooksAccountCatalogResponse>;
  }

  getWallitSupplierCandidates(): Promise<WallitSupplierCandidatesResponse> {
    return this.quickBooksResource().one('wallit-supplier-candidates').get().toPromise() as
      Promise<WallitSupplierCandidatesResponse>;
  }

  getWallitCompanyCandidates(): Promise<WallitCompanyCandidatesResponse> {
    return this.quickBooksResource().one('wallit-company-candidates').get().toPromise() as
      Promise<WallitCompanyCandidatesResponse>;
  }

  getVendorCatalog(): Promise<QuickBooksVendorCatalogResponse> {
    return this.quickBooksResource().one('vendor-catalog').get().toPromise() as
      Promise<QuickBooksVendorCatalogResponse>;
  }

  getCustomerCatalog(): Promise<QuickBooksCustomerCatalogResponse> {
    return this.quickBooksResource().one('customer-catalog').get().toPromise() as
      Promise<QuickBooksCustomerCatalogResponse>;
  }

  startAuthorization(): Promise<AuthorizationStartResponse> {
    return this.quickBooksResource().one('oauth').one('authorize').customPOST({}).toPromise() as Promise<AuthorizationStartResponse>;
  }

  removeConnection(): Promise<QuickBooksConnectionStatus> {
    return this.quickBooksResource().one('connection').one('remove').customPOST({}).toPromise() as Promise<QuickBooksConnectionStatus>;
  }

  retryCleanup(): Promise<QuickBooksConnectionStatus> {
    return this.quickBooksResource().one('connection').one('cleanup-retry').customPOST({}).toPromise() as Promise<QuickBooksConnectionStatus>;
  }

  getSanitizedErrorMessage(error: any, fallback: string): string {
    const code = error?.data?.code || error?.error?.code || '';
    const messages: Record<string, string> = {
      QUICKBOOKS_ACCOUNT_CATALOG_UNAVAILABLE: 'The complete QuickBooks Account catalog is temporarily unavailable.',
      QUICKBOOKS_ACTIVE_CONNECTION_EXISTS: 'A QuickBooks connection already exists.',
      QUICKBOOKS_AUTHORIZATION_REQUIRES_REPLACEMENT: 'Authorization is no longer usable. Remove the connection and add it again.',
      QUICKBOOKS_CLEANUP_NOT_REQUIRED: 'No QuickBooks connection currently requires cleanup.',
      QUICKBOOKS_CONNECTION_NOT_FOUND: 'No active QuickBooks connection was found.',
      QUICKBOOKS_CUSTOMER_CATALOG_UNAVAILABLE: 'The complete QuickBooks Customer catalog is temporarily unavailable.',
      QUICKBOOKS_REFRESH_RECONCILIATION_REQUIRED: 'Token refresh could not be confirmed. Remove the connection and add it again.',
      QUICKBOOKS_REFRESH_IN_PROGRESS: 'QuickBooks connection maintenance is already in progress. Try again shortly.',
      QUICKBOOKS_UNAVAILABLE: 'QuickBooks connection management is temporarily unavailable.',
      QUICKBOOKS_VENDOR_CATALOG_UNAVAILABLE: 'The complete QuickBooks Vendor catalog is temporarily unavailable.',
      QUICKBOOKS_WALLIT_CANDIDATES_UNAVAILABLE: 'Wallit comparison candidates are temporarily unavailable.',
    };
    return messages[code] || fallback;
  }

  private quickBooksResource(): any {
    return this.restangular.one('accounting').one('quickbooks');
  }
}
